import { User } from '@/types/user';
import { UserModule, Module } from '@/hooks/useUserModule';

/**
 * Configuration for the mock system when ?noguard is used.
 * This allows easy addition of new routes without modifying core logic.
 */

export interface ModuleConfig {
  name: string;
  screenType: string;
  surveyType?: string;
}

export const moduleConfigs: Record<string, ModuleConfig> = {
  profile: { name: 'Profile Setup', screenType: 'profile' },
  pulse: { name: 'PULSE Assessment', screenType: 'survey_intro', surveyType: 'pulse' },
  shape: { name: 'SHAPE Assessment', screenType: 'survey_intro', surveyType: 'shape' },
  'pulse-survey': { name: 'PULSE Survey', screenType: 'survey', surveyType: 'pulse' },
  'shape-survey': { name: 'SHAPE Survey', screenType: 'survey', surveyType: 'shape' },
  coaching: { name: 'AI Coaching', screenType: 'chat' },
  dashboard: { name: 'Dashboard', screenType: 'dashboard' },
};

export const routePatterns: Array<[RegExp, string]> = [
  [/^\/profile/, 'profile'],
  [/^\/survey\/pulse\/\d+/, 'pulse-survey'],
  [/^\/survey\/shape\/\d+/, 'shape-survey'],
  [/^\/survey\/pulse$/, 'pulse'],
  [/^\/survey\/shape$/, 'shape'],
  [/^\/coaching/, 'coaching'],
  [/^\/module\?.*type=chat/, 'coaching'],  // Support ?type=chat parameter for testing
  [/^\/updates/, 'dashboard'],
];

export function getModuleTypeFromPath(pathname: string): string | null {
  for (const [pattern, moduleType] of routePatterns) {
    if (pattern.test(pathname)) {
      return moduleType;
    }
  }
  return null;
}

export function inferOnboardingStatus(pathname: string): string {
  if (pathname.includes('/profile')) return 'profile';
  if (pathname.includes('/survey')) return 'survey';
  if (pathname.includes('/coaching')) return 'coaching';
  if (pathname.includes('/module') && pathname.includes('type=chat')) return 'coaching';  // Only for testing
  if (pathname.includes('/updates')) return 'completed';
  return 'welcome';
}

export function createMockUser(pathname: string): User {
  return {
    id: 'mock-user-1',
    email: 'dev@example.com',
    firstName: 'Dev',
    lastName: 'User',
    title: 'Developer',
    department: 'Engineering',
    onboardingStatus: inferOnboardingStatus(pathname),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function createMockModule(pathname: string): UserModule | null {
  const moduleType = getModuleTypeFromPath(pathname);
  if (!moduleType || moduleType === 'dashboard') {
    return null; // No current module for dashboard/completed state
  }

  const config = moduleConfigs[moduleType];
  if (!config) return null;

  // Determine order based on module type
  const orderMap: Record<string, number> = {
    profile: 1,
    pulse: 2,
    'pulse-survey': 2,
    shape: 3,
    'shape-survey': 3,
    coaching: 4,
  };

  const order = orderMap[moduleType] || 1;
  const moduleId = `${moduleType}-module`;
  const surveyId = config.surveyType ? `${config.surveyType}-survey` : undefined;

  // Add mock data for chat modules
  let moduleData: string | undefined;
  if (config.screenType === 'survey_intro') {
    moduleData = JSON.stringify({ surveyType: config.surveyType });
  } else if (config.screenType === 'chat') {
    moduleData = JSON.stringify({
      welcomeMessage: "Welcome to your AI coaching session! I'm here to help you with your professional development and any challenges you're facing.",
      suggestedPrompts: [
        "I'd like to discuss my career goals",
        "I'm facing a challenge at work",
        "Help me improve my leadership skills"
      ],
      minMessages: 3,
      allowSkip: true
    });
  }

  const module: Module = {
    id: order,
    name: config.name,
    introText: `Welcome to ${config.name}`,
    sequence: order,
    isActive: true,
    isRequired: true,
    isPaywalled: false,
    screenType: config.screenType,
    surveyId: config.surveyType === 'pulse' ? 1 : config.surveyType === 'shape' ? 2 : null,
    data: moduleData,
  };

  return {
    id: order,
    userId: 1,
    moduleId: order,
    status: 'IN_PROGRESS',
    module,
  };
}

export function createMockSurveyQuestions(surveyType: string) {
  const questions = surveyType === 'pulse' ? [
    { id: 1, questionText: 'How energized do you feel?', category: 'energy' },
    { id: 2, questionText: 'How well are you sleeping?', category: 'sleep' },
    { id: 3, questionText: 'How is your stress level?', category: 'stress' },
  ] : [
    { id: 1, questionText: 'How often do you exercise?', category: 'activity' },
    { id: 2, questionText: 'How would you rate your diet?', category: 'nutrition' },
    { id: 3, questionText: 'How connected do you feel to others?', category: 'social' },
  ];

  return questions.map((q, index) => ({
    ...q,
    surveyId: surveyType === 'pulse' ? 1 : 2,
    order: index + 1,
  }));
}